import React, { useState, useEffect } from "react";
import { IconButton, Divider, Menu, MenuItem } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import icons from "../../assets/icons/icon-collection.svg";
import smallpholder from "../../assets/icons/small-placeholder.svg";
import placeholder from "../../assets/icons/placeholder.svg";
import Notifications from "./Notifications";
import { Link, useHistory } from "react-router-dom";
import { logoutAction } from "../../redux/actions/authActions";
import { ternaryResolver } from "../../utils/helpers";
import CustomInput from "../../components/CustomInput";
import {
  clearJobCreated,
  searchJobAction,
} from "../../redux/actions/jobActions";
import { searchJobAction as businessSearchJobAction } from "../../redux/actions/businessActions";
import AddNewPost from "../NewPost";

const ToolbarMenu = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  // States
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [newPost, setNewPost] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { userData } = useSelector((store) => store.auth);
  const { first_name, last_name, image_url } = userData;
  const resolvedName = ternaryResolver(first_name, `${first_name}`, "");

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNewPost = () => {
    clearJobCreated(dispatch);
    setNewPost(true);
  };

  const handleNewPostClose = () => {
    setSuccess(false);
    setNewPost(false);
  };

  const isBusinessUser = () => {
    const accountType = localStorage.getItem("accountType");
    return accountType === "business";
  };

  const handleSearch = async () => {
    // If search bar is open and has text, perform search
    if (searchTerm.trim()) {
      setIsSearching(true);
      
      // Callback to execute after Redux action finishes
      const onSearchFinish = () => {
        // Slight timeout ensures the user sees the "Searching..." state briefly for better UX
        setTimeout(() => {
          setIsSearching(false);
          history.push("/home");
        }, 600);
      };

      try {
        if (isBusinessUser()) {
          businessSearchJobAction(dispatch, searchTerm, onSearchFinish);
        } else {
          // Assuming searchJobAction returns a promise or uses the callback
          await searchJobAction(dispatch, searchTerm, onSearchFinish);
        }
      } catch (error) {
        console.error("Search failed:", error);
        setIsSearching(false);
      }
    } else {
      // Toggle search bar visibility if empty
      setShowSearchBar(!showSearchBar);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="w-100">
      <div className={`d-flex align-items-center justify-content-end ${isMobile ? "flex-wrap" : ""}`}>
        
        {/* Search Input Field */}
        {showSearchBar && (
          <div className={`search-container ${isMobile ? "w-100 order-1 mb-2" : "w-50"}`}>
            <CustomInput
              placeholder={isSearching ? "Searching..." : "Search"}
              additionalClass="dark-border"
              id="search-job"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              isDisabled={isSearching} // Gray out input while loading
            />
          </div>
        )}

        {/* Icons and Actions */}
        <div className={`d-flex align-items-center ${isMobile && showSearchBar ? "order-2 w-100 justify-content-between" : ""}`}>
          <div className="d-flex align-items-center">
            
            <IconButton 
              onClick={handleSearch} 
              disabled={isSearching}
              className="search-button"
            >
              {isSearching ? (
                <div className="search-spinner-container">
                  <div className="spinner-ring"></div>
                </div>
              ) : (
                <svg width="18" height="16">
                  <use href={`${icons}#search`}></use>
                </svg>
              )}
            </IconButton>

            <IconButton onClick={(e) => setNotificationsAnchorEl(e.currentTarget)}>
              <svg width="16" height="18">
                <use href={`${icons}#bell`}></use>
              </svg>
            </IconButton>

            <IconButton className="d-md-none" onClick={handleNewPost}>
              <svg width="12" height="12">
                <use href={`${icons}#plus`}></use>
              </svg>
            </IconButton>
          </div>

          <AddNewPost
            open={newPost}
            handleClose={handleNewPostClose}
            setSuccess={setSuccess}
            success={success}
          />
          
          <Notifications
            anchorEl={notificationsAnchorEl}
            handleClose={() => setNotificationsAnchorEl(null)}
          />

          {!isMobile && <Divider orientation="vertical" flexItem />}

          {/* Profile Section */}
          <div className="d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={(e) => setAnchorEl(e.currentTarget)}>
            <img
              src={image_url || placeholder}
              width={isMobile ? "32" : "40"}
              height={isMobile ? "32" : "40"}
              style={{
                borderRadius: "100px",
                marginLeft: isMobile ? "8px" : "10px",
                objectFit: "cover"
              }}
              alt="Profile"
              onError={({ target }) => { target.src = placeholder; }}
            />
            {!isMobile && <span className="mx-2">{resolvedName}</span>}
            <IconButton size="small">
              <svg width="8" height="6">
                <use href={`${icons}#arrowdown`}></use>
              </svg>
            </IconButton>
          </div>
        </div>

        {/* Profile Dropdown Menu */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            style: {
              width: isMobile ? "90vw" : "15rem",
              maxWidth: "15rem",
              marginTop: "0.5rem",
              borderRadius: "1rem",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.1)"
            },
          }}
        >
          <MenuItem disabled>
            <img
              src={image_url || smallpholder}
              width="30" height="30"
              style={{ borderRadius: "100px" }}
              alt=""
            />
            <span className="mx-2" style={{ color: '#000', fontWeight: 'bold' }}>{resolvedName}</span>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => setAnchorEl(null)} component={Link} to="/profile">
            <span className="px-2 py-1 medium-text">Profile</span>
          </MenuItem>
          <MenuItem onClick={() => setAnchorEl(null)} component={Link} to="/settings/help">
            <span className="px-2 py-1 medium-text">Help/Support</span>
          </MenuItem>
          <MenuItem onClick={() => setAnchorEl(null)} component={Link} to="/settings/about">
            <span className="px-2 py-1 medium-text">About workbrook</span>
          </MenuItem>
          <MenuItem onClick={() => setAnchorEl(null)} component={Link} to="/settings/security">
            <span className="px-2 py-1 medium-text">Change Password</span>
          </MenuItem>
          <Divider />
          <MenuItem onClick={logoutAction} className="px-4 py-3 co-lightred medium-text">
            Logout of workbrook
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default ToolbarMenu;